import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { PdfPageLivePreview } from './PdfPageLivePreview';
import {
  PDF_IMPORT_CANVAS_BACKGROUND,
  PDF_IMPORT_PAGE_MAX_WIDTH_PX,
  PDF_IMPORT_PAGE_STACK_GAP_PX,
} from './pdf-import.constants';

type PdfContinuousPageViewerProps = {
  doc: PDFDocumentProxy;
  pageCount: number;
  /** Page currently considered in view (drives thumbs + “N of M”). */
  activePageIndex: number;
  onActivePageChange: (pageIndex: number) => void;
  /**
   * When this value changes, scroll that page into view (thumbnail click).
   * Distinct from {@link activePageIndex} so scroll-driven updates do not loop.
   */
  scrollRequestPageIndex?: number;
  scrollRequestId?: number;
};

type PageSize = {
  width: number;
  height: number;
};

/** Fallback A4-ish ratio while pdf.js page metrics load. */
const FALLBACK_PAGE_SIZE: PageSize = { width: 210, height: 297 };

/**
 * Continuous-scroll PDF page stack (standard viewer). Active page tracks scroll;
 * external scroll requests come from the thumbnail rail.
 */
export const PdfContinuousPageViewer: React.FC<
  PdfContinuousPageViewerProps
> = ({
  doc,
  pageCount,
  activePageIndex,
  onActivePageChange,
  scrollRequestPageIndex,
  scrollRequestId,
}) => {
  const { classes } = useStyles();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const ratiosRef = useRef<Map<number, number>>(new Map());
  const observerRef = useRef<IntersectionObserver | undefined>(undefined);
  const onActivePageChangeRef = useRef(onActivePageChange);
  onActivePageChangeRef.current = onActivePageChange;
  /** Ignore intersection updates while jumping from a thumbnail click. */
  const ignoreScrollSyncRef = useRef(false);
  const ignoreScrollSyncTimerRef = useRef<number | undefined>(undefined);
  const [pageSizes, setPageSizes] = useState<PageSize[]>([]);

  // Natural page sizes so slots hug each page (portrait / landscape) like a PDF preview.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const sizes: PageSize[] = [];
      for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
        const page = await doc.getPage(pageIndex + 1);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: 1 });
        sizes.push({ width: viewport.width, height: viewport.height });
      }
      if (!cancelled) setPageSizes(sizes);
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, pageCount]);

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;

    const ratios = ratiosRef.current;
    ratios.clear();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const raw = (entry.target as HTMLElement).dataset.pageIndex;
          if (raw === undefined) continue;
          const pageIndex = Number(raw);
          if (!Number.isFinite(pageIndex)) continue;
          ratios.set(
            pageIndex,
            entry.isIntersecting ? entry.intersectionRatio : 0,
          );
        }
        let bestPage = 0;
        let bestRatio = -1;
        for (const [pageIndex, ratio] of ratios) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = pageIndex;
          }
        }
        // Thumbnail jumps must not walk focus through intermediate pages.
        if (ignoreScrollSyncRef.current) return;
        if (bestRatio > 0) onActivePageChangeRef.current(bestPage);
      },
      { root, threshold: [0.15, 0.35, 0.55, 0.75] },
    );
    observerRef.current = observer;

    for (const element of pageRefs.current.values()) {
      observer.observe(element);
    }

    return () => {
      observer.disconnect();
      observerRef.current = undefined;
    };
  }, [pageCount]);

  useEffect(() => {
    if (scrollRequestPageIndex === undefined) return;
    const target = pageRefs.current.get(scrollRequestPageIndex);
    if (!target) return;
    ignoreScrollSyncRef.current = true;
    if (ignoreScrollSyncTimerRef.current !== undefined) {
      window.clearTimeout(ignoreScrollSyncTimerRef.current);
    }
    // Instant jump — smooth scroll made focus flash through every page.
    target.scrollIntoView({ block: 'start', behavior: 'auto' });
    ignoreScrollSyncTimerRef.current = window.setTimeout(() => {
      ignoreScrollSyncRef.current = false;
      ignoreScrollSyncTimerRef.current = undefined;
    }, 120);
    return () => {
      if (ignoreScrollSyncTimerRef.current !== undefined) {
        window.clearTimeout(ignoreScrollSyncTimerRef.current);
        ignoreScrollSyncTimerRef.current = undefined;
      }
    };
  }, [scrollRequestId, scrollRequestPageIndex]);

  return (
    <div ref={scrollerRef} className={classes.scroller}>
      {Array.from({ length: pageCount }, (_, pageIndex) => {
        // Keep a window of live renders around the active page (more pages fit on screen).
        const isNear =
          Math.abs(pageIndex - activePageIndex) <= 2 || pageCount <= 5;
        const size = pageSizes[pageIndex] ?? FALLBACK_PAGE_SIZE;
        return (
          <div
            key={pageIndex}
            ref={(node) => {
              const previous = pageRefs.current.get(pageIndex);
              if (previous && previous !== node) {
                observerRef.current?.unobserve(previous);
                pageRefs.current.delete(pageIndex);
              }
              if (node) {
                pageRefs.current.set(pageIndex, node);
                observerRef.current?.observe(node);
              }
            }}
            data-page-index={pageIndex}
            className={classes.pageSlot}
          >
            <div
              className={classes.pageFrame}
              style={{
                aspectRatio: `${size.width} / ${size.height}`,
              }}
            >
              {isNear ? (
                <PdfPageLivePreview
                  className={classes.pagePreview}
                  doc={doc}
                  pageIndex={pageIndex}
                />
              ) : (
                <div className={classes.pagePlaceholder} aria-hidden />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  scroller: {
    display: 'flex',
    flexDirection: 'column',
    // Flex gap — not margin — so canvas overflow cannot eat the spacing.
    gap: PDF_IMPORT_PAGE_STACK_GAP_PX,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    padding: theme.spacing(1, 0),
    backgroundColor: PDF_IMPORT_CANVAS_BACKGROUND,
  },
  pageSlot: {
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    // Same inset as the stack gap — side margins match space between pages.
    padding: `0 ${PDF_IMPORT_PAGE_STACK_GAP_PX}px`,
  },
  pageFrame: {
    width: '100%',
    maxWidth: PDF_IMPORT_PAGE_MAX_WIDTH_PX,
    // Height comes from the page aspect ratio.
    position: 'relative',
    // Clip pdf.js canvas so it cannot paint over the stack gap.
    overflow: 'hidden',
    '& > *': {
      width: '100%',
      height: '100%',
    },
  },
  pagePreview: {
    overflow: 'hidden',
  },
  pagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.common.white,
  },
}));
